import { useState, useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info, AlertCircle, Phone, Mail } from "lucide-react";

interface PreCheckSystemProps {
  onComplete: (financialYear: string) => void;
}

export function PreCheckSystem({ onComplete }: PreCheckSystemProps) {
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [hasIncompleteReturns, setHasIncompleteReturns] = useState(false);
  const { toast } = useToast();

  // Calculate available financial years
  const getAvailableYears = () => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1; // JavaScript months are 0-based
    const currentYear = currentDate.getFullYear();

    const years = [];
    if (currentMonth >= 7) {
      // After July 1st, current FY is this year-next year
      years.push(`${currentYear}-${currentYear + 1}`);
      years.push(`${currentYear - 1}-${currentYear}`);
      years.push(`${currentYear - 2}-${currentYear - 1}`);
    } else {
      // Before July 1st, current FY is previous year-this year
      years.push(`${currentYear - 1}-${currentYear}`);
      years.push(`${currentYear - 2}-${currentYear - 1}`);
      years.push(`${currentYear - 3}-${currentYear - 2}`);
    }
    return years;
  };

  // Check for incomplete returns
  useEffect(() => {
    const checkIncompleteReturns = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) throw new Error("No authenticated user found");

        const { data: returns, error } = await supabase
          .from("tax_return_requests")
          .select("status")
          .eq("user_id", user.id)
          .not("status", "in", '("completed","rejected")');

        if (error) throw error;
        setHasIncompleteReturns(returns.length > 0);
      } catch (error) {
        console.error("Error checking incomplete returns:", error);
      }
    };

    checkIncompleteReturns();
  }, []);

  const handleYearSelect = async (year: string) => {
    setSelectedYear(year);

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No authenticated user found");

      // Log pre-check attempt
      const { error } = await supabase.from("tax_return_pre_checks").insert({
        user_id: user.id,
        financial_year: year,
        check_result: true,
        metadata: { has_incomplete_returns: hasIncompleteReturns },
      });

      if (error) {
        console.error("Error logging pre-check:", error);
        throw error;
      }
    } catch (error) {
      console.error("Error logging pre-check:", error);
    }
  };

  const handleProceed = () => {
    if (!selectedYear) {
      toast({
        title: "Select Financial Year",
        description: "Please select a financial year to proceed.",
        variant: "destructive",
      });
      return;
    }

    if (hasIncompleteReturns && !acknowledged) {
      toast({
        title: "Acknowledgment Required",
        description:
          "Please acknowledge the incomplete returns warning to proceed.",
        variant: "destructive",
      });
      return;
    }

    onComplete(selectedYear);
  };

  const availableYears = getAvailableYears();
  const isOldYear =
    selectedYear && !availableYears.slice(0, 2).includes(selectedYear);

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Info className="h-5 w-5" />
          Tax Return Pre-Check
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Select Financial Year</label>
            <Select onValueChange={handleYearSelect} value={selectedYear}>
              <SelectTrigger>
                <SelectValue placeholder="Select financial year" />
              </SelectTrigger>
              <SelectContent>
                {availableYears.map((year) => (
                  <SelectItem key={year} value={year}>
                    {year} Financial Year
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isOldYear && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="ml-2">
                For returns earlier than {availableYears[1]}, please contact us:
                <div className="mt-2 space-y-1">
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    <span>1300 123 435</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    <span>tax.agents@numberz.au</span>
                  </div>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {hasIncompleteReturns && (
            <div className="flex items-start space-x-2">
              <Checkbox
                id="incomplete"
                checked={acknowledged}
                onCheckedChange={(checked) =>
                  setAcknowledged(checked as boolean)
                }
              />
              <label
                htmlFor="incomplete"
                className="text-sm text-muted-foreground leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                I acknowledge that I have incomplete tax returns and wish to
                proceed with a new submission
              </label>
            </div>
          )}
        </div>

        <Button
          onClick={handleProceed}
          disabled={
            !selectedYear ||
            (hasIncompleteReturns && !acknowledged) ||
            isOldYear
          }
          className="w-full"
        >
          Start New Tax Return
        </Button>
      </CardContent>
    </Card>
  );
}
